import {createSelector} from '@reduxjs/toolkit'

import {isKoreanIncluded} from '@shared/helpers/lang'
import {someValueKeys} from '@shared/helpers/object'

import {
    DELIVERY_MEMO,
    GeneralCountryCodeList,
    LOT_NUMBER_ADDRESS_ZIPCODE_LENGTH,
    MaxLength,
    ZipCodeSize,
} from '@web/core/constants'
import {
    checkMemoLength,
    checkMemoIncludesInvisibleExceptSpace,
    isUserPhoneNumberInBlacklist,
    isValidPhoneNumberPrefix,
    isValidUserPhoneNumber,
} from '@web/helpers/policies'

import {
    DeliveryValidationCode,
    fillBaseAddressNeededFailType,
    suggestBannerShowNeededFailType,
    VALIDATION_SUCCESS,
} from './constant'
import {DeliveryInfoState, OldMemoInfo, RootState} from './interfaces'
import {convertZipCode, createMemoOptionList, getInitialState} from './utils'

/** 배송 정보 selector */
export const deliveryInfoSelector = (state: RootState) => state.deliveryInfo

/** 배송 불가 사유 selector */
export const deliveryDisabledReasonSelector = (state: RootState) => state.deliveryInfo.disabledReason

/** 배송 정보 selector */
export const deliveryOptionalInfoSelector = createSelector(
    (state: {deliveryInfo?: DeliveryInfoState}) => state.deliveryInfo,
    (deliveryInfo) => {
        return deliveryInfo ?? getInitialState()
    },
)

const isGeneralCountryCode = (countryCode: string) => GeneralCountryCodeList.includes(countryCode)

/** 배송지 정보 selector */
export const addressesInfoSelector = createSelector(
    (state: RootState) => state.deliveryInfo,
    (state: RootState) => state.deliveryInfo.selected,
    (state: RootState) => state.deliveryInfo.pickup,
    (
        {hasDefaultAddress, virtualPhoneNumber, zipCodeValidation: {validationFailType}},
        {addressInfo: selectedAddress},
        pickup,
    ) => {
        const deliveryAddress = {
            addressName: selectedAddress?.addressName ?? '',
            receiverName: selectedAddress?.receiverName ?? '',
            zipCode: convertZipCode(selectedAddress?.zipCode),
            buildingManagementNo: selectedAddress?.buildingManagementNo ?? '',
            locationX: selectedAddress?.locationX ?? '',
            locationY: selectedAddress?.locationY ?? '',
            baseAddress: selectedAddress?.baseAddress ?? '',
            detailAddress: selectedAddress?.detailAddress ?? '',
            road: selectedAddress?.isRoadNameAddress ?? false,
            telNo1: selectedAddress?.telNo1 ?? '',
            telNo2: selectedAddress?.telNo2 ?? '',
        }

        const [telNo1GeneralCountryCode] = deliveryAddress.telNo1.split('-')
        const [telNo2GeneralCountryCode, telNo2First, telNo2Secend] = deliveryAddress.telNo2.split('-')

        return {
            /** 배송지 주소 존재여부 */
            hasDeliveryAddress: !!selectedAddress,
            /** 기본 배송지 보유 여부 */
            hasDefaultDeliveryAddress: hasDefaultAddress,
            /** 최근 배송지 목록에서 선택된 배송지 정보 */
            selectedAddress,
            /** 수령위치 */
            pickUpLocation: pickup.location,
            /** 출입방법 */
            entryMethod: pickup.entryMethod,
            /** 다음에도 사용할게요 체크박스 */
            alwaysUses: pickup.alwaysUses,
            /** 주소 해시값 */
            hash: selectedAddress?.hash || '',
            deliveryAddress,
            /** 일반 국번 포함 여부 */
            hasGeneralCountryCode:
                (telNo1GeneralCountryCode && isGeneralCountryCode(telNo1GeneralCountryCode)) ||
                (telNo2GeneralCountryCode && isGeneralCountryCode(telNo2GeneralCountryCode)),
            /** 일반 국번 미포함 여부 */
            hasNotGeneralCountryCode:
                !telNo1GeneralCountryCode ||
                (telNo1GeneralCountryCode && !isGeneralCountryCode(telNo1GeneralCountryCode)) ||
                ((!!telNo2Secend || !!telNo2First) && !telNo2GeneralCountryCode) ||
                (telNo2GeneralCountryCode && !isGeneralCountryCode(telNo2GeneralCountryCode)),
            /** 장보기 상품 수령 정보 */
            pickup,
            addressChangeSuggestBanner: {
                show: suggestBannerShowNeededFailType.some((type) => type === validationFailType),
                shouldSetInitialZipCodeSearchKeyword: fillBaseAddressNeededFailType.some(
                    (type) => type === validationFailType,
                ),
            },
            /** 안심번호 */
            virtualPhoneNumber: virtualPhoneNumber.value,
        }
    },
)

/** 배송지 메모 info selector */
export const deliveryMemoInfoSelector = createSelector(
    (state: RootState) => state.deliveryInfo.memo,
    ({isParticularInput, particularInputByItemId, individuallyInput, list, saveAsDefaultMemo}) => {
        const particularInputWithRequiredMemoType = Object.entries(particularInputByItemId).reduce<{
            [itemId: string]: OldMemoInfo
        }>((acc, [itemId, particularInput]) => {
            acc[itemId] = {
                ...particularInput,
                type: particularInput.type ?? DELIVERY_MEMO.INITIAL,
            }

            return acc
        }, {})

        return {
            isParticularInput,
            individuallyInput,
            /** memo.type이 필수로 있는 particularInputByItemId */
            particularInputByItemId: particularInputWithRequiredMemoType,
            /** itemId를 index로 치환한 메모 목록 */
            memosWithItemIdAsIndex: Object.values(particularInputByItemId),
            /** 기본 제공 배송 메모 목록 */
            memoOptions: createMemoOptionList(list),
            list,
            saveAsDefaultMemo,
        }
    },
)

export const baseDeliveryInfoValidationSelector = createSelector(
    (state: RootState) => state.deliveryInfo.selected.addressInfo,
    (state: RootState) => state.deliveryInfo.deprecatedZipCodes,
    (state: RootState) => state.deliveryInfo.hasArrivalGuaranteeProduct,
    (state: RootState) => state.deliveryInfo.hasOverseasDeliveryProduct,
    (addressInfo, deprecatedZipCodes, hasArrivalGuaranteeProduct, hasOverseasDeliveryProduct) => {
        if (!addressInfo) {
            return DeliveryValidationCode.DELIVERY_INPUT_FAIL
        }

        const {addressName, baseAddress, zipCode, receiverName, telNo1, telNo2, isRoadNameAddress, detailAddress} =
            addressInfo

        if (!baseAddress || !zipCode) {
            return DeliveryValidationCode.DELIVERY_INPUT_FAIL
        }

        if (addressName && addressName.length > MaxLength.ADDRESS_NAME) {
            return DeliveryValidationCode.DELIVERY_ADDRESS_NAME_MAX_FAIL
        }

        if (!receiverName) {
            return DeliveryValidationCode.DELIVERY_RECEIVER_NAME_FAIL
        }

        if (receiverName.length > MaxLength.RECEIVER_NAME) {
            return DeliveryValidationCode.DELIVERY_RECEIVER_NAME_MAX_FAIL
        }

        if (detailAddress && detailAddress.length > MaxLength.DETAIL_ADDRESS) {
            return DeliveryValidationCode.DELIVERY_DETAIL_ADDRESS_MAX_FAIL
        }

        if (
            isUserPhoneNumberInBlacklist(telNo1) ||
            !isValidUserPhoneNumber(telNo1) ||
            !isValidPhoneNumberPrefix(telNo1.split('-')[0] ?? '')
        ) {
            return DeliveryValidationCode.DELIVERY_PHONE_NUMBER_FAIL
        }

        if (
            telNo2 &&
            (isUserPhoneNumberInBlacklist(telNo2) ||
                !isValidUserPhoneNumber(telNo2) ||
                !isValidPhoneNumberPrefix(telNo2.split('-')[0] ?? ''))
        ) {
            return DeliveryValidationCode.DELIVERY_PHONE_NUMBER_TWO_FAIL
        }

        if (!isKoreanIncluded(baseAddress)) {
            return DeliveryValidationCode.DELIVERY_KOREA_ADDRESS_FAIL
        }

        if (!isRoadNameAddress && !detailAddress) {
            return DeliveryValidationCode.DELIVERY_LOT_DETAIL_NONE
        }

        if (deprecatedZipCodes.includes(zipCode)) {
            return DeliveryValidationCode.DEPRECATED_ZIPCODES
        }

        if (hasOverseasDeliveryProduct && (zipCode.length !== ZipCodeSize.FIVE || !isRoadNameAddress)) {
            return DeliveryValidationCode.OVERSEA_DELIVERY_NOT_ROAD_ADDRESS
        }

        if (hasArrivalGuaranteeProduct && zipCode.length === ZipCodeSize.SIX) {
            return DeliveryValidationCode.REJECT_SIX_DIGITS_ZIPCODE_WITH_ARRIVAL_GUARANTEE_PRODUCT
        }

        if (zipCode.length === LOT_NUMBER_ADDRESS_ZIPCODE_LENGTH) {
            return DeliveryValidationCode.REJECT_LOT_NUMBER_ADDRESS
        }

        return VALIDATION_SUCCESS
    },
)

export const baseDeliveryInfoValidationWarningSelector = createSelector(
    (state: RootState) => state.deliveryInfo,
    ({selected: {addressInfo}}) => {
        if (addressInfo && addressInfo.isRoadNameAddress && !addressInfo.detailAddress) {
            return DeliveryValidationCode.ROAD_NAME_ADDRESS_EMPTY_DETAIL_ADDRESS
        }

        return VALIDATION_SUCCESS
    },
)

export const memoValidationSelector = createSelector(
    (state: RootState) => state.deliveryInfo.memo,
    ({isParticularInput, particularInputByItemId, individuallyInput}) => {
        if (
            isParticularInput
                ? particularInputByItemId &&
                  someValueKeys(({value}) => {
                      return !!value && checkMemoLength(value)
                  }, particularInputByItemId)
                : individuallyInput && checkMemoLength(individuallyInput.value)
        ) {
            return DeliveryValidationCode.MEMO_MAX_FAIL
        }

        if (
            (individuallyInput && checkMemoIncludesInvisibleExceptSpace(individuallyInput.value)) ||
            (particularInputByItemId &&
                Object.values(particularInputByItemId).some(({value}) => checkMemoIncludesInvisibleExceptSpace(value)))
        ) {
            return DeliveryValidationCode.MEMO_INCLUDE_INVISIBLE_CHAR_FAIL
        }

        return VALIDATION_SUCCESS
    },
)

export const deliveryShippingSelector = (state: RootState) => state.deliveryInfo.shipping
